// @ts-strict-ignore
import { useTranslation } from "react-i18next";

import { AppleSSOButton } from "@shared/components/LoginOptions/AppleSSOButton/AppleSSOButton";
import { GoogleSSOButton } from "@shared/components/LoginOptions/GoogleSSOButton/GoogleSSOButton";
import { fullWidthButton } from "@shared/components/LoginOptions/LoginOptions.styles";
import { OrSection } from "@shared/components/LoginOptions/OrSection/OrSection";
import { useFlippers } from "@shared/context/FlippersContext";
import { useQualityStorySignupExperiment } from "@shared/hooks/useQualityStorySignupExperiment/useQualityStorySignupExperiment";
import { FormWrapper } from "@unauthenticated/shared/components/FormWrapper/FormWrapper";
import { SignInWrapper } from "@unauthenticated/shared/components/SignInWrapper/SignInWrapper";
import { SSOErrorBox } from "@unauthenticated/shared/components/SSOErrorBox/SSOErrorBox";
import type { PatientAttributes } from "@unauthenticated/shared/data/types/PatientAttributes.d";
import { useTreatmentPlanTerm } from "@unauthenticated/shared/hooks/useTreatmentPlanTerm/useTreatmentPlanTerm";
import type { SubmitValues } from "@unauthenticated/signupSignin/components/PatientSignUpForm/PatientSignUpForm";
import { PatientSignUpForm } from "@unauthenticated/signupSignin/components/PatientSignUpForm/PatientSignUpForm";
import { PoweredByFullscript } from "@unauthenticated/signupSignin/components/PoweredByFullscript/PoweredByFullscript";
import { SignUpContainer } from "@unauthenticated/signupSignin/components/SignUpContainer/SignUpContainer";
import { SignUpHeader } from "@unauthenticated/signupSignin/components/SignUpHeader/SignUpHeader";
import { SignUpSubtitle } from "@unauthenticated/signupSignin/components/SignUpSubtitle/SignUpSubtitle";
import { SignUpTitle } from "@unauthenticated/signupSignin/components/SignUpTitle/SignUpTitle";
import { StoreLandingLayout } from "@unauthenticated/signupSignin/components/StoreLandingLayout/StoreLandingLayout";
import { StoreLogo } from "@unauthenticated/signupSignin/components/StoreLogo/StoreLogo";
import { StoreOffersPill } from "@unauthenticated/signupSignin/components/StoreOffersPill/StoreOffersPill";
import { usePatientSignUpUnauthenticatedSignUpSignInMutation } from "@unauthenticated/signupSignin/data/mutations/PatientSignUp.mutation.generated";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

import * as sharedStyles from "../storeLandingScenes.styles";

import * as styles from "./PatientStoreActivationPage.styles";

interface Props {
  patientAttributes: PatientAttributes;
  additionalAttributes?: any;
  pageVersion: "react_with_sso" | "react";
  ssoErrors?: string;
  storeId?: string;
  activePatientPromotion?: boolean;
  isStorefronts?: boolean;
}

const PatientStoreActivationPage = ({
  patientAttributes,
  additionalAttributes,
  pageVersion,
  ssoErrors,
  activePatientPromotion,
  storeId,
  isStorefronts,
}: Props) => {
  const [isVanguardUhCustomExperienceEnabled] = useFlippers("vanguard_uh_custom_experience");
  const isQualityStorySignupEnabled = useQualityStorySignupExperiment({
    shouldLog: !isStorefronts,
  });
  const { t } = useTranslation();
  const treatmentPlanTerm = useTreatmentPlanTerm();

  const SIGN_UP_PAGE = "rx_patient_sign_up_page";

  const {
    storeLogo,
    organizationLogo,
    storeName,
    firstName,
    storeSlug,
    treatmentPlanSlug,
    labRecommendationId,
    pracName,
    uuid,
    iconLogo,
  } = patientAttributes;

  const [signUp, { loading, data }] = usePatientSignUpUnauthenticatedSignUpSignInMutation({
    onCompleted: payload => {
      if (payload.auth.patientSignUpFromRx.redirectPath) {
        window.location.assign(payload.auth.patientSignUpFromRx.redirectPath);
      }
    },
  });

  const handleSubmit = (values: SubmitValues) => {
    const { gToken, ...attributesFromForm } = values;
    const attributes = {
      ...attributesFromForm,
      treatmentPlanSlug,
      labRecommendationId,
      storeSlug,
      uuid,
      optional: additionalAttributes,
    };
    signUp({ variables: { input: { attributes, captchaToken: gToken } } });
  };

  const signupModalSubtitle = labRecommendationId
    ? t(l.patientRxAuth.SignUpWelcomeMessageLabRecommendation, {
        firstName,
        pracName,
      })
    : t(l.patientRxAuth.SignUpWelcomeMessage, {
        firstName,
        treatmentPlanTerm,
        pracName,
      });

  const showQualityStoryChrome = !isStorefronts && isQualityStorySignupEnabled;

  const renderActivationForm = (isFullBleed: boolean) => {
    const ssoButtonStyles = isFullBleed ? fullWidthButton : undefined;

    const formBlock = (
      <FormWrapper>
        <SignInWrapper>
          <SSOErrorBox ssoErrors={ssoErrors} />
          <GoogleSSOButton
            buttonStyles={ssoButtonStyles}
            storeSlug={storeSlug}
            treatmentPlanSlug={treatmentPlanSlug}
            labRecommendationId={labRecommendationId}
            signUpPage={SIGN_UP_PAGE}
            pageVersion={pageVersion}
            uuid={uuid}
          />
          <AppleSSOButton
            css={styles.appleButton}
            buttonStyles={ssoButtonStyles}
            storeSlug={storeSlug}
            treatmentPlanSlug={treatmentPlanSlug}
            labRecommendationId={labRecommendationId}
            signUpPage={SIGN_UP_PAGE}
            pageVersion={pageVersion}
            uuid={uuid}
          />
          <div css={styles.orWrapper}>
            <OrSection />
          </div>
          <PatientSignUpForm
            onSubmit={handleSubmit}
            isLoading={loading}
            errors={data?.auth?.patientSignUpFromRx?.errors?.fields}
            pageVersion={pageVersion}
            signUpPage={SIGN_UP_PAGE}
            isFullWidthButtons={isFullBleed}
          />
        </SignInWrapper>
      </FormWrapper>
    );

    const poweredByBadge = showQualityStoryChrome ? <PoweredByFullscript /> : null;

    return (
      <>
        <SignUpHeader hasCompactSpacing={showQualityStoryChrome}>
          <StoreLogo storeLogo={storeLogo} storeName={storeName} />
          <SignUpTitle title={storeName} />
          <SignUpSubtitle subtitle={signupModalSubtitle} />
          {!labRecommendationId && !isVanguardUhCustomExperienceEnabled && (
            <StoreOffersPill patientAttributes={patientAttributes} />
          )}
        </SignUpHeader>
        {isFullBleed ? (
          <>
            <div css={sharedStyles.headerSpacer} />
            {formBlock}
            {poweredByBadge}
          </>
        ) : (
          <SignUpContainer>
            {formBlock}
            {poweredByBadge}
          </SignUpContainer>
        )}
      </>
    );
  };

  return (
    <StoreLandingLayout
      storeName={storeName}
      storeId={storeId}
      storeSlug={storeSlug}
      pracAvatar={patientAttributes.pracAvatar}
      isActivePromotion={activePatientPromotion}
      isStorefronts={isStorefronts}
      organizationLogo={organizationLogo}
      iconLogo={iconLogo}
      hasFullBleedPanel
    >
      {({ isFullBleed }) => renderActivationForm(isFullBleed)}
    </StoreLandingLayout>
  );
};

export { PatientStoreActivationPage };
